/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState, forwardRef } from 'react'
import styled from 'styled-components'
import accounting from 'accounting'
import { MqDesktop } from 'streamr-ui/css/consts'
import UnstyledLink from '~shared/Link'
import useIsMounted from '~hooks/useIsMounted'
import Display from '~shared/Display'
import ProgressCounter from '~shared/ProgressCounter'
import { getDataFundBalance, DATA_FUND_CONTRACT_ADDRESS } from '~utils/ethereum'
import useRealtimeDataTokenStream from '~hooks/useRealtimeDataTokenStream'

const MILLION = 1000000
const TOTAL_BALANCE = 11226620 // Initial balance

const Separator = styled.div`
    flex-grow: 1;
    height: 2em;
    position: relative;

    ::after {
        background-color: #cdcdcd;
        content: '';
        height: 100%;
        left: 50%;
        position: absolute;
        width: 1px;
    }
`

const Link = styled(UnstyledLink)`
    color: #0324ff !important;
`

function formatNumber(number, precision) {
    const abbreviations = ['', 'k', 'm', 'b', 't']
    const order = Math.floor(Math.log10(Math.abs(number)) / 3)
    const matchedOrder = Math.max(0, Math.min(order, abbreviations.length - 1))
    const suffix = abbreviations[matchedOrder]
    return (number / 10 ** (matchedOrder * 3)).toFixed(precision) + suffix
}

const UnstyledCounter = forwardRef(function UnstyledCounter({ className, style }, ref) {
    const [currentBalance, setCurrentBalance] = useState(0)
    const [grantedBalance, setGrantedBalance] = useState(0)
    const [hasLoaded, setHasLoaded] = useState(false)
    const isMounted = useIsMounted()

    const { DATAUSDPoint } = useRealtimeDataTokenStream()

    useEffect(() => {
        const loadEthereumData = async () => {
            const balance = await (async () => {
                try {
                    return await getDataFundBalance()
                } catch (e) {
                    console.error('Failed to load balance.', e)
                }

                // Let's display 0% if things fail to load.
                return TOTAL_BALANCE
            })()

            if (isMounted()) {
                setCurrentBalance(balance)
                setGrantedBalance(TOTAL_BALANCE - balance)
                setHasLoaded(true)
            }
        }

        loadEthereumData()
    }, [isMounted])

    return (
        <ProgressCounter
            className={className}
            loading={!hasLoaded}
            title="Total funds granted"
            value={grantedBalance / TOTAL_BALANCE}
            supply={
                <>
                    <Display mobile="none" tablet="block">
                        {accounting.formatMoney(TOTAL_BALANCE, {
                            symbol: 'DATA',
                            format: '%v %s',
                            precision: 0,
                            thousand: ' ',
                        })}
                    </Display>
                    <Display mobile="block" tablet="none">
                        {(TOTAL_BALANCE / MILLION).toFixed(0)}M DATA
                    </Display>
                </>
            }
            ref={ref}
            style={style}
        >
            <ProgressCounter.Hub>
                <ProgressCounter.HubItem>
                    <strong>
                        Granted {formatNumber(grantedBalance, 1)}{' '}
                        <Display mobile="none" tablet="inline-block">
                            {' '}
                            DATA
                        </Display>
                    </strong>
                    {!!DATAUSDPoint && (
                        <>
                            {' '}
                            <Display as="span" mobile="none" desktop="inline-block">
                                {formatNumber(grantedBalance * DATAUSDPoint, 1)} USD
                            </Display>
                        </>
                    )}
                </ProgressCounter.HubItem>
                <Separator />
                <ProgressCounter.HubItem>
                    <strong>
                        Balance {formatNumber(currentBalance, 1)}{' '}
                        <Display mobile="none" tablet="inline-block">
                            {' '}
                            DATA
                        </Display>
                    </strong>
                    {!!DATAUSDPoint && (
                        <>
                            {' '}
                            <Display as="span" mobile="none" desktop="inline-block">
                                {formatNumber(currentBalance * DATAUSDPoint, 1)} USD
                            </Display>
                        </>
                    )}
                </ProgressCounter.HubItem>
                <Separator />
                <ProgressCounter.HubItem>
                    <Link
                        href={`https://etherscan.io/address/${DATA_FUND_CONTRACT_ADDRESS}`}
                        newTab
                        noDecoration
                    >
                        <Display as="span" mobile="inline" tablet="none">
                            Etherscan
                        </Display>
                        <Display as="span" mobile="none" tablet="inline" desktop="none">
                            View on Etherscan
                        </Display>
                        <Display as="span" mobile="none" desktop="inline">
                            View contract on Etherscan
                        </Display>
                    </Link>
                </ProgressCounter.HubItem>
            </ProgressCounter.Hub>
        </ProgressCounter>
    )
})

const Counter = styled(UnstyledCounter)`
    ${ProgressCounter.Hub} {
        align-items: center;
        display: flex;
        margin-top: 24px;
    }

    ${ProgressCounter.Hub} strong {
        color: #323232;
    }

    @media ${MqDesktop} {
        ${ProgressCounter.Hub} {
            margin-top: 40px;
        }
    }
`

export default Counter
